/**
 * Session Service Module
 * Manages verification session state throughout the SDK lifecycle.
 *
 * This is a standalone service that can be used for custom verification flows
 * or as a reference implementation. The main AppBouncer class manages its own
 * session state internally, but this service provides a cleaner interface for
 * session management in custom implementations.
 */

import { VerificationMode } from '../orchestration';

// ============================================================
// Types
// ============================================================

export interface SessionLivenessActionResult {
  action: string;
  ok: boolean;
}

export interface SessionLivenessResult {
  passed: boolean;
  score?: number;
  actions?: SessionLivenessActionResult[];
}

export interface SessionAgeResult {
  estimate: number;
  confidence: number;
}

export interface SessionDocumentResult {
  verified: boolean;
  extractedData?: Record<string, string>;
}

export interface SessionFaceMatchResult {
  matched: boolean;
  confidence?: number;
}

export interface SessionResults {
  liveness?: SessionLivenessResult;
  age?: SessionAgeResult;
  document?: SessionDocumentResult;
  faceMatch?: SessionFaceMatchResult;
}

export interface VerificationSession {
  sessionId: string;
  challengeId?: string;
  token?: string;
  startedAt: number;
  expiresAt?: number;
  mode: VerificationMode;
  results: SessionResults;
}

export interface SessionServiceConfig {
  /** Default verification mode */
  defaultMode?: VerificationMode;
  /** Debug mode for logging */
  debug?: boolean;
}

// ============================================================
// Session Service Class
// ============================================================

/**
 * Manages verification session lifecycle and state.
 * Provides methods to create, update, and retrieve session data.
 */
export class SessionService {
  private config: SessionServiceConfig;
  private currentSession: VerificationSession | null = null;

  constructor(config: SessionServiceConfig = {}) {
    this.config = {
      defaultMode: 'full',
      debug: false,
      ...config,
    };
  }

  /**
   * Create a new verification session
   */
  createSession(mode?: VerificationMode): VerificationSession {
    this.currentSession = {
      sessionId: this.generateSessionId(),
      startedAt: Date.now(),
      mode: mode ?? this.config.defaultMode ?? 'full',
      results: {},
    };

    this.log('Session created:', this.currentSession.sessionId);
    return this.currentSession;
  }

  /**
   * Get the current session
   */
  getSession(): VerificationSession | null {
    return this.currentSession ? { ...this.currentSession } : null;
  }

  /**
   * Check if a session exists
   */
  hasSession(): boolean {
    return this.currentSession !== null;
  }

  /**
   * Ensure a session exists, creating one if needed
   */
  ensureSession(mode?: VerificationMode): VerificationSession {
    if (!this.currentSession) {
      this.log('Auto-creating session');
      return this.createSession(mode);
    }
    return this.currentSession;
  }

  /**
   * Set the challenge ID for the current session
   */
  setChallengeId(challengeId: string): void {
    if (this.currentSession) {
      this.currentSession.challengeId = challengeId;
      this.log('Challenge ID set:', challengeId);
    }
  }

  /**
   * Set the token for the current session
   */
  setToken(token: string): void {
    if (this.currentSession) {
      this.currentSession.token = token;
      this.log('Token set');
    }
  }

  /**
   * Set the expiration timestamp
   */
  setExpiresAt(expiresAt: number): void {
    if (this.currentSession) {
      this.currentSession.expiresAt = expiresAt;
    }
  }

  /**
   * Update liveness results
   */
  setLivenessResult(result: SessionLivenessResult): void {
    if (this.currentSession) {
      this.currentSession.results.liveness = result;
      this.log('Liveness result set:', result.passed);
    }
  }

  /**
   * Get liveness result
   */
  getLivenessResult(): SessionLivenessResult | null {
    return this.currentSession?.results.liveness ?? null;
  }

  /**
   * Update age estimation results
   */
  setAgeResult(result: SessionAgeResult): void {
    if (this.currentSession) {
      this.currentSession.results.age = result;
      this.log('Age result set:', result.estimate);
    }
  }

  /**
   * Get age result
   */
  getAgeResult(): SessionAgeResult | null {
    return this.currentSession?.results.age ?? null;
  }

  /**
   * Update document verification results
   */
  setDocumentResult(result: SessionDocumentResult): void {
    if (this.currentSession) {
      this.currentSession.results.document = result;
      this.log('Document result set:', result.verified);
    }
  }

  /**
   * Get document result
   */
  getDocumentResult(): SessionDocumentResult | null {
    return this.currentSession?.results.document ?? null;
  }

  /**
   * Update face match results
   */
  setFaceMatchResult(result: SessionFaceMatchResult): void {
    if (this.currentSession) {
      this.currentSession.results.faceMatch = result;
      this.log('Face match result set:', result.matched);
    }
  }

  /**
   * Get face match result
   */
  getFaceMatchResult(): SessionFaceMatchResult | null {
    return this.currentSession?.results.faceMatch ?? null;
  }

  /**
   * Clear liveness results (for retry)
   */
  clearLivenessResult(): void {
    if (this.currentSession?.results) {
      this.currentSession.results.liveness = undefined;
      this.log('Liveness result cleared');
    }
  }

  /**
   * Clear the current session
   */
  clearSession(): void {
    const sessionId = this.currentSession?.sessionId;
    this.currentSession = null;
    if (sessionId) {
      this.log('Session cleared:', sessionId);
    }
  }

  /**
   * Get all session results
   */
  getResults(): SessionResults {
    return this.currentSession?.results ?? {};
  }

  /**
   * Check if session has all required results
   */
  hasAllResults(required: Array<keyof SessionResults>): boolean {
    if (!this.currentSession) return false;
    return required.every((key) => this.currentSession!.results[key] !== undefined);
  }

  // ============================================================
  // Private Methods
  // ============================================================

  private generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private log(...args: unknown[]): void {
    if (this.config.debug) {
      console.log('[SessionService]', ...args);
    }
  }
}
